import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ConfirmationService, LazyLoadEvent, MessageService, SelectItem } from 'primeng';
import { Subject, Subscription } from 'rxjs';
import { Message } from 'src/app/classes/Message';
import {
  INonMovementTransaction,
  INonMovementTransactionHistory,
} from 'src/app/interfaces/non-movement-transaction';
import { INonMovementTransactionLinesFilter } from 'src/app/interfaces/non-movement-transaction-lines-filter';
import { IOrganisationFilter } from 'src/app/interfaces/organisation-filter';
import { IPageResult } from 'src/app/interfaces/page-result';
import { ISystemSettings } from 'src/app/interfaces/system-settings';
import { DataImportService } from 'src/app/services/data-import.service';
import { NonMovementDescriptionService } from 'src/app/services/non-movement-description.services';
import { NonMovementTransactionService } from 'src/app/services/non-movement-transaction.services';
import { OrganisationsService } from 'src/app/services/organisations.service';
import { SettingsService } from 'src/app/services/settings.service';
import { flatten } from '../../../utils/flatten';
import { debounceTime } from 'rxjs/operators';
import { CustomerService } from '../../../services';

@Component({
  selector: 'app-create-non-movement-transactions',
  templateUrl: './create-non-movement-transactions.component.html',
  styleUrls: ['./create-non-movement-transactions.component.css'],
})
export class CreateNonMovementTransactionsComponent implements OnInit, OnDestroy {
  //public
  public filterVal: INonMovementTransactionLinesFilter;
  public filter: Subject<INonMovementTransactionLinesFilter>;

  private _manageMode: number;
  @Input('manageMode')
  set manageMode(value: number) {
    this._manageMode = value;
  }
  get manageMode(): number {
    return this._manageMode;
  }
  public page: IPageResult<INonMovementTransaction> = {
    items: [],
    total: 0,
  };
  cols: any[];
  public nonTransactionForm: UntypedFormGroup;
  public organisationTypes: any[];
  public contractTypes: any[];
  public organisations: any[];
  public transactionPrefixes: any[];
  public descriptionTypes: any[];
  public isTransactionFormEnabled: boolean;
  public selectedTransactions: INonMovementTransaction[];
  public todayDate: Date;
  public maxBackDate: Date;
  public selectedOrganisationId: number;
  public savingTransactions: boolean;
  public transactionHistory: INonMovementTransactionHistory[];
  public transactionHistoryTimeline: any[];
  public enableTransactionHistoryDialog: boolean = false;
  public maxBackDateTransactionDays: number;
  public showUploadNonMovementTransactionsDialog: boolean;
  public toggleTransactionLines: boolean;
  public isProcessing: boolean;
  public progressBarTitle: string;
  public sample_spreadsheet_file = '/assets/docs/non_movement_transaction_sample.csv';
  private subscription: Subscription;
  departmentsFilterList: SelectItem[] = [];
  departmentsDialogList: SelectItem[] = [];
  constructor(
    private organisationService: OrganisationsService,
    private nonMovementTransactionService: NonMovementTransactionService,
    private nonMovementDescriptionService: NonMovementDescriptionService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private settingsService: SettingsService,
    private dataImportService: DataImportService,
    private customerService: CustomerService,
  ) {}

  async ngOnInit() {
    this.initDefaultFilter();
    this.organisationTypes = [];
    this.contractTypes = [];
    this.organisations = [];
    this.transactionPrefixes = [];
    this.descriptionTypes = [];
    this.isTransactionFormEnabled = false;
    this.resetTransactionForm();
    this.todayDate = new Date();
    this.maxBackDate = new Date();
    this.filter = new Subject<INonMovementTransactionLinesFilter>();
    this.filter.pipe(debounceTime(500)).subscribe((f) => {
      this.loadTransactions(f);
      this.filterVal = f;
    });
    this.getTransactionDescription();
    this.getTransactionPrefixes();
    this.getMaxBackDateForTransactions();

    const departmentsOnLoad = await this.getDepartmentsForOrganisationId(null, false);
    this.departmentsDialogList = departmentsOnLoad;
    this.departmentsFilterList = departmentsOnLoad;

    this.cols = [
      { field: 'transactionNumber', header: 'Transaction Number' },
      { field: 'organisationId', header: 'Organisation' },
      { field: 'departmentName', header: 'Department' },
      { field: 'transactionLinePrefixId', header: 'Prefix Type' },
      { field: 'amount', header: 'Amount' },
      { field: 'createdOn', header: 'Transaction Created' },
      { field: 'descriptionId', header: 'Description' },
    ];
    // for data import, auto reload items to show imported items
    this.dataImportService.imported$.subscribe(() => {
      this.reloadTransactions();
    });
  }

  initDefaultFilter() {
    this.filterVal = {
      first: 0,
      rows: 10,
      sortField: null,
      sortOrder: 1,
      filters: {
        from: { value: null },
        to: { value: null },
        organisationId: { value: null },
        transactionTypeId: { value: null },
        awaitinginvoice: { value: null },
        departmentId: { value: null },
      },
    };
  }

  loadTransactions(filter: INonMovementTransactionLinesFilter) {
    this.nonMovementTransactionService
      .getAll(flatten(filter))
      .then((result) => {
        this.page = result;
      })
      .catch((err) => {
        console.error(err);
      });
  }

  search = () => this.reloadTransactions();

  handleOrganisationFilterChange() {
    if (this.nonTransactionForm && this.nonTransactionForm.value.organisationTypeId) {
      var organisationFilter: IOrganisationFilter = {
        filters: {
          organisationTypeId: { value: this.nonTransactionForm.value.organisationTypeId },
          contractTypeId: { value: this.nonTransactionForm.value.contractTypeId },
        },
      };
      this.organisations = [];
      this.organisationService
        .getSelectList(organisationFilter)
        .then((res) => {
          if (res) {
            let orgList = [];
            orgList.push({ label: 'Please Select Organisation', value: null });
            res.forEach((x) => {
              orgList.push(x);
            });
            this.organisations = orgList;
          } else {
            console.log('Organisations: ', res);
          }
        })
        .catch((err) => {
          console.log('Error fetching Organisations');
          this.messageService.add(Message.Error(err.error, 'Error'));
        });
    }
  }

  getTransactionPrefixes() {
    this.nonMovementTransactionService
      .getPrefixes(true)
      .then((res) => {
        if (res) {
          console.log(res);
          this.transactionPrefixes.push({ label: 'Please Select Transaction Prefix', value: null });
          res
            .sort((x) => x.id)
            .forEach((x) => {
              this.transactionPrefixes.push({ label: x.name, value: x.id });
            });
        } else {
          console.log('Transaction Prefixes: ', res);
        }
      })
      .catch((err) => {
        console.log('Error fetching Transaction Prefixes');
        this.messageService.add(Message.Error(err.error, 'Error'));
      });
  }

  getTransactionDescription() {
    this.nonMovementDescriptionService
      .getAll()
      .then((res) => {
        if (res) {
          this.descriptionTypes.push({ label: 'Please Select Description', value: null });
          res
            .sort((x) => x.id)
            .forEach((x) => {
              this.descriptionTypes.push({ label: x.description, value: x.id });
            });
        } else {
          console.log('Description Type: ', res);
        }
      })
      .catch((err) => {
        console.log('Error fetching Description Types');
        this.messageService.add(Message.Error(err.error, 'Error'));
      });
    this.nonMovementDescriptionService
      .getAll()
      .then((res) => {
        if (res) {
          this.descriptionTypes.push({ label: 'Please Select Xero Code', value: null });
          res
            .sort((x) => x.id)
            .forEach((x) => {
              this.descriptionTypes.push({ label: x.description, value: x.id });
            });
        } else {
          console.log('Description Type: ', res);
        }
      })
      .catch((err) => {
        console.log('Error fetching Xero Code Types');
        this.messageService.add(Message.Error(err.error, 'Error'));
      });
  }

  getMaxBackDateForTransactions() {
    this.settingsService.GetSystemSettings().then((data: ISystemSettings) => {
      if (data && data.maxBackDateNonMovementTransactionInDays) {
        this.maxBackDateTransactionDays = data.maxBackDateNonMovementTransactionInDays;
        this.maxBackDate.setDate(this.todayDate.getDate() - this.maxBackDateTransactionDays);
      } else {
        this.messageService.add(
          Message.Error('Max Back Date Value for Non-Movement Transactions is required', 'Error'),
        );
      }
    });
  }

  saveTransaction(formValues) {
    if (formValues && this.nonTransactionForm.valid) {
      formValues.createdOn = formValues.createdOn.toDateString(); // lose the Timezone info
      this.savingTransactions = true;
      if (!formValues.id) {
        this.nonMovementTransactionService
          .create(formValues)
          .then((res) => {
            console.log(res);
            this.reloadTransactions();
            this.isTransactionFormEnabled = this.savingTransactions = false;
          })
          .catch((err) => {
            console.log(err);
            this.savingTransactions = false;
            this.messageService.add(Message.Error(err.error, 'Error'));
          });
      } else {
        this.nonMovementTransactionService
          .update(formValues.id, formValues)
          .then((res) => {
            console.log(res);
            this.reloadTransactions();
            this.isTransactionFormEnabled = this.savingTransactions = false;
          })
          .catch((err) => {
            console.log(err);
            this.savingTransactions = false;
            this.messageService.add(Message.Error(err.error, 'Error'));
          });
      }
    }
  }
  resetTransactionForm() {
    this.nonTransactionForm = new UntypedFormGroup({
      id: new UntypedFormControl(0),
      transactionLinePrefixId: new UntypedFormControl(
        null,
        Validators.compose([Validators.required]),
      ),
      organisationId: new UntypedFormControl(null, Validators.compose([Validators.required])),
      departmentId: new UntypedFormControl(null),
      amount: new UntypedFormControl(null, Validators.compose([Validators.required])),
      createdOn: new UntypedFormControl(null, Validators.compose([Validators.required])),
      descriptionId: new UntypedFormControl(null, Validators.compose([Validators.required])),
      customDescription: new UntypedFormControl(null),
    });
  }
  showTransactionFormDialog = () => {
    this.resetTransactionForm();
    this.isTransactionFormEnabled = true;
  };

  async modifyTransaction(transaction) {
    this.showTransactionFormDialog();
    if (transaction) {
      console.log(transaction);
      this.nonTransactionForm = new UntypedFormGroup({
        id: new UntypedFormControl(transaction.id),
        descriptionId: new UntypedFormControl(
          transaction.descriptionId,
          Validators.compose([Validators.required]),
        ),
        createdOn: new UntypedFormControl(
          new Date(transaction.createdOn),
          Validators.compose([Validators.required]),
        ),
        amount: new UntypedFormControl(
          transaction.amount,
          Validators.compose([Validators.required]),
        ),
        transactionLinePrefixId: new UntypedFormControl(
          transaction.transactionLinePrefixId,
          Validators.compose([Validators.required]),
        ),
        organisationId: new UntypedFormControl(
          transaction.organisationId,
          Validators.compose([Validators.required]),
        ),
        transactionNumber: new UntypedFormControl(transaction.transactionNumber),
        customDescription: new UntypedFormControl(transaction.customDescription),
        departmentId: new UntypedFormControl(transaction.departmentId),
      });

      this.handleOrganisationFilterChange(); // this to refetch the organisations
      this.departmentsDialogList = [
        { label: transaction.departmentName, value: transaction.departmentId },
      ];
      this.departmentsDialogList = await this.getDepartmentsForOrganisationId(
        transaction.organisationId,
        true,
      );
    }
  }

  handleTransactionSelectionChange() {
    this.selectedTransactions = this.page.items.filter((x) => x.isChecked);
  }

  async onOrganisationChange(organisationId: number) {
    this.departmentsFilterList = [{ label: 'Loading...', value: 0, disabled: true }];
    this.filterVal.filters.organisationId.value = organisationId;

    this.departmentsFilterList = await this.getDepartmentsForOrganisationId(organisationId, false);
  }

  onDepartmentChange(departmentId: number) {
    this.filterVal.filters.departmentId.value = departmentId;
  }

  async onNewTransactionOrganisationChange(organisationId: number) {
    this.departmentsDialogList = [{ label: 'Loading...', value: 0, disabled: true }];
    this.nonTransactionForm.value.organisationId = organisationId;

    this.departmentsDialogList = await this.getDepartmentsForOrganisationId(organisationId, true);
  }

  deleteTransaction(transaction) {
    if (transaction.id) {
      this.confirmationService.confirm({
        message: `Confirm you want to delete selected transaction for ${transaction.organisation.name} and amount ${transaction.amount}?`,
        key: 'confirmDialog',
        accept: () => {
          this.nonMovementTransactionService
            .delete(transaction.id)
            .then((res) => {
              this.reloadTransactions();
            })
            .catch((err) => {
              console.log(err);
              this.messageService.add(Message.Error(err.error, 'Error'));
            });
        },
        reject: () => console.log('User selected to not delete transaction'),
      });
    }
  }

  deleteSelectedTransactions() {
    if (this.toggleTransactionLines) {
      this.confirmationService.confirm({
        message: 'Confirm you want to delete all selected transaction',
        key: 'confirmDialog',
        accept: () => {
          let ids = this.selectedTransactions.map((trans) => {
            return trans.id;
          });
          this.progressBarTitle = 'Deleting Selected Transactions';
          this.isProcessing = true;
          this.nonMovementTransactionService
            .deleteTransactions(ids)
            .then((res) => {
              this.reloadTransactions();
              this.isProcessing = false;
              this.toggleTransactionLines = false;
              this.progressBarTitle = '';
              this.messageService.add(
                Message.Success('Transactions Deleted Successfully', 'Transactions Deletion'),
              );
            })
            .catch((err) => {
              this.messageService.add(Message.Error(err.error, 'Error'));
              this.isProcessing = false;
              this.progressBarTitle = '';
            });
        },
        reject: () => console.log('User selected to not delete transactions'),
      });
    }
  }

  processSelectedTransactions() {
    this.confirmationService.confirm({
      message: `Confirm you want to process selected transaction(s)?`,
      key: 'confirmDialog',
      accept: () => {
        let ids = this.selectedTransactions.map((trans) => {
          return trans.id;
        });
        this.nonMovementTransactionService
          .processTransactions(ids)
          .then((res) => {
            this.reloadTransactions();
          })
          .catch((err) => {
            console.log(err);
          });
      },
      reject: () => console.log('User selected to not process transactions'),
    });
  }

  onGridLazyLoad(event: LazyLoadEvent) {
    this.filterVal.first = event.first;
    this.filterVal.rows = event.rows;
    this.filterVal.sortField = event.sortField;
    this.filterVal.sortOrder = event.sortOrder;
    this.filter.next(this.filterVal);
  }

  reloadTransactions = () => {
    this.selectedTransactions = [];
    this.filterVal.first = 0;
    this.filter.next(this.filterVal);
  };

  toggleTransactionsCheckbox() {
    if (this.toggleTransactionLines) {
      this.page.items = this.page.items.map((value, index) => {
        if (!value.isInvoiced && !value.transactionLinesId) {
          value.isChecked = true;
        }
        return value;
      });
    } else {
      this.page.items.forEach((x) => (x.isChecked = false));
    }

    this.handleTransactionSelectionChange();
  }

  ngOnDestroy() {
    if (this.subscription) this.subscription.unsubscribe();
  }
  showHistory(nonMovementTransactionId: number) {
    //fetch history if any
    this.enableTransactionHistoryDialog = false;
    this.nonMovementTransactionService
      .getHistory(nonMovementTransactionId)
      .then((res) => {
        if (res) {
          this.transactionHistoryTimeline =
            this.nonMovementTransactionService.getHistoryTimelineFromTransactionHistories(res);
          this.enableTransactionHistoryDialog = true;
        } else {
          //show error that nothin was returned
          this.messageService.add(Message.Error('History not found for Transaction', 'Error'));
        }
      })
      .catch((err) => {
        this.messageService.add(Message.Error(err.error, 'Error'));
      });
  }

  private async getDepartmentsForOrganisationId(
    orgId: number,
    filterForInvoiceAddress: boolean = false,
  ): Promise<SelectItem[]> {
    if (orgId == null) {
      return [
        { label: 'Please select an organisation to view departments', value: 0, disabled: true },
      ];
    }

    const departments = await this.customerService.getDepartments(orgId);
    if (departments.length == 0) {
      return [{ label: 'No results found for organisation', value: 0, disabled: true }];
    }

    return filterForInvoiceAddress
      ? departments
          .filter((department) => department.invoiceAddressId != null)
          .map((department) => ({ label: department.name, value: department.id }))
      : departments.map((department) => ({ label: department.name, value: department.id }));
  }
}
